import { GenericError } from "@via/components/error.tsx";
import { SkeletonLoader } from "@via/components/loading/loading.tsx";
import { ERROR } from "@via/utils/constants";
import { formatDateString } from "@via/utils/helpers";
import React from "react";
import { Controller, type Control } from "react-hook-form";
import Datepicker, {
  DatepickerType,
  DateType,
  DateValueType,
} from "react-tailwindcss-datepicker";
import "./date-picker.css";

type DatePickerProps = Omit<DatepickerType, "value" | "onChange"> & {
  label?: string;
  control?: Control<any>;
  name?: string;
  rules?: Record<string, unknown>;
  isLoading?: boolean;
  isError?: boolean;
  value?: DateValueType | string;
  onChange?: (value: DateValueType | string | null) => void;
};

// Helper: Check if date value is null or empty
const isNullOrEmpty = (value: DateValueType): boolean => {
  return value?.startDate === null && value?.endDate === null;
};

// Helper: Convert string date to DateValueType format for the library
const convertToDateValueType = (
  value: DateValueType | string | undefined | unknown,
  asSingle?: boolean
): DateValueType | null => {
  if (asSingle === true && typeof value === "string") {
    return {
      startDate: value as unknown as DateType,
      endDate: value as unknown as DateType,
    };
  }
  return (value as DateValueType) ?? null;
};

// Helper: Format date value based on asSingle mode
const formatDateValue = (
  value: DateValueType,
  asSingle?: boolean
): string | { startDate: DateType; endDate: DateType } => {
  if (asSingle) {
    return formatDateString(
      value?.startDate as unknown as string
    ) as unknown as string;
  }

  return {
    startDate: formatDateString(
      value?.startDate as unknown as string
    ) as unknown as DateType,
    endDate: formatDateString(
      value?.endDate as unknown as string
    ) as unknown as DateType,
  };
};

const DatePicker: React.FC<DatePickerProps> = ({
  control,
  name,
  rules,
  label,
  isLoading,
  isError,
  ...defaultProps
}) => {
  const isControlled = !!control && !!name;

  const onChange = (value: DateValueType) => {
    if (isNullOrEmpty(value)) {
      defaultProps.onChange &&
        defaultProps.onChange(defaultProps.asSingle ? null : value);
      return;
    }

    // Return string for asSingle mode, DateValueType for range mode
    const formattedValue = formatDateValue(value, defaultProps.asSingle);
    defaultProps.onChange && defaultProps.onChange(formattedValue);
  };

  const controlledOnChange = (field: {
    onChange: (value: string | DateValueType | null) => void;
  }) => {
    return (value: DateValueType) => {
      if (isNullOrEmpty(value)) {
        field.onChange(defaultProps.asSingle ? null : value);
        return;
      }

      // For controlled mode (with react-hook-form), return string for asSingle, DateValueType for range
      const formattedValue = formatDateValue(value, defaultProps.asSingle);
      field.onChange(formattedValue);
    };
  };

  return (
    <>
      {label && <label className="via-label">{label ?? "Description"}</label>}
      <>
        {isLoading ? (
          <div className="py-6">
            <SkeletonLoader count={1} />
          </div>
        ) : isError ? (
          <div className="via-generic-error lg:py-12">
            <GenericError message={ERROR} />
          </div>
        ) : isControlled ? (
          <Controller
            control={control}
            name={name}
            rules={rules}
            render={({ field }) => (
              <Datepicker
                containerClassName="datepicker-container relative"
                inputClassName="w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                {...defaultProps}
                value={convertToDateValueType(
                  field.value,
                  defaultProps.asSingle
                )}
                onChange={controlledOnChange(field)}
              />
            )}
          />
        ) : (
          <Datepicker
            containerClassName="datepicker-container relative"
            inputClassName="w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            {...defaultProps}
            value={convertToDateValueType(
              defaultProps.value,
              defaultProps.asSingle
            )}
            onChange={onChange}
          />
        )}
      </>
    </>
  );
};

export default DatePicker;
